import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { CircleCheck, TriangleAlert, Send } from "lucide-react";
import { SubpageHeader } from "../components/SubpageHeader";
import { Reveal } from "../components/Reveal";
import { useLanguage } from "../i18n/LanguageContext";
import { useSEO, breadcrumbLd } from "../components/SEO";
import { track } from "../analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactoPage() {
    const { t } = useLanguage();
    useSEO({
        title: t.seo.contacto.title,
        description: t.seo.contacto.desc,
        jsonLd: breadcrumbLd([{ name: t.bc.home, path: "/" }, { name: t.bc.contacto, path: "/contacto" }]),
    });
    const [form, setForm] = useState({ nome: "", email: "", assuntoIdx: 0, mensagem: "" });
    const [estado, setEstado] = useState("form"); // form | enviando | sucesso
    const [erro, setErro] = useState(null);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submeter = async (e) => {
        e.preventDefault();
        setErro(null);
        setEstado("enviando");
        try {
            await axios.post(`${API}/contactos`, {
                nome: form.nome,
                email: form.email,
                assunto: t.contacto.assuntos[form.assuntoIdx],
                mensagem: form.mensagem,
            });
            setEstado("sucesso");
            track("Contact", { assunto: t.contacto.assuntos[form.assuntoIdx] }, { email: form.email });
        } catch (err) {
            const d = err.response?.data?.detail;
            setErro(typeof d === "string" ? d : t.contacto.erroGenerico);
            setEstado("form");
        }
    };

    return (
        <div className="min-h-screen bg-[#070503] text-[#efe6d6] relative overflow-hidden" data-testid="contacto-page">
            {/* Fundo fixo: a imagem fica parada e a informação rola por cima */}
            <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true">
                <img loading="eager" decoding="async" src="/images/contacto-fundo.webp" alt="" className="w-full h-full object-cover object-center" data-testid="contacto-fundo" />
                <div className="absolute inset-0 bg-[#070503]/40" />
            </div>
            <SubpageHeader />
            <motion.main
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 mx-auto max-w-[720px] px-5 sm:px-10 pt-44 sm:pt-32 pb-24"
            >
                <Reveal className="text-center">
                    <h1 className="font-display font-medium gold-text text-4xl sm:text-5xl tracking-[0.08em]" data-testid="contacto-title">
                        {t.contacto.titulo}
                    </h1>
                    <div className="flex justify-center mt-4">
                        <img loading="lazy" decoding="async" src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-16 opacity-90" />
                    </div>
                    <p className="font-display font-light text-lg text-[#efe6d6]/70 leading-relaxed max-w-xl mx-auto mt-6">
                        {t.contacto.intro}
                    </p>
                    <p className="font-light text-xs text-[#efe6d6]/45 tracking-wide mt-6" data-testid="contacto-associacao">
                        {t.footer.sede}
                    </p>
                </Reveal>

                {estado === "sucesso" ? (
                    <Reveal className="mt-12">
                        <div className="border border-[#c9a24b]/40 bg-[#0a0705]/80 p-10 text-center" data-testid="contacto-sucesso">
                            <CircleCheck size={44} strokeWidth={1.2} className="text-[#c9a24b] mx-auto" />
                            <h2 className="font-display font-light text-2xl sm:text-3xl text-[#efe6d6] mt-6">{t.contacto.sucessoTitulo}</h2>
                            <p className="font-light text-[#efe6d6]/70 mt-4 leading-relaxed">
                                {t.contacto.sucessoTexto.replace("{nome}", form.nome.split(" ")[0])}
                            </p>
                        </div>
                    </Reveal>
                ) : (
                    <Reveal delay={0.15} className="mt-10">
                        <form onSubmit={submeter} className="border border-[#c9a24b]/30 bg-[#0a0705]/80 p-8 sm:p-10 space-y-6" data-testid="contacto-form">
                            <div className="grid sm:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="ct-nome" className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f] block mb-2.5">
                                        {t.contacto.nome}
                                    </label>
                                    <input
                                        id="ct-nome"
                                        type="text"
                                        required
                                        minLength={2}
                                        value={form.nome}
                                        onChange={set("nome")}
                                        data-testid="contacto-nome"
                                        className="w-full bg-[#070503] border border-[#c9a24b]/30 px-4 py-3 font-light text-[#efe6d6] placeholder:text-[#efe6d6]/30 focus:border-[#c9a24b] focus:outline-none transition-colors duration-300"
                                        placeholder={t.contacto.nomePh}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="ct-email" className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f] block mb-2.5">
                                        {t.contacto.email}
                                    </label>
                                    <input
                                        id="ct-email"
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={set("email")}
                                        data-testid="contacto-email"
                                        className="w-full bg-[#070503] border border-[#c9a24b]/30 px-4 py-3 font-light text-[#efe6d6] placeholder:text-[#efe6d6]/30 focus:border-[#c9a24b] focus:outline-none transition-colors duration-300"
                                        placeholder={t.contacto.emailPh}
                                    />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="ct-assunto" className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f] block mb-2.5">
                                    {t.contacto.assunto}
                                </label>
                                <select
                                    id="ct-assunto"
                                    value={form.assuntoIdx}
                                    onChange={set("assuntoIdx")}
                                    data-testid="contacto-assunto"
                                    className="w-full bg-[#070503] border border-[#c9a24b]/30 px-4 py-3 font-light text-[#efe6d6] focus:border-[#c9a24b] focus:outline-none transition-colors duration-300"
                                >
                                    {t.contacto.assuntos.map((a, i) => (
                                        <option key={a} value={i}>{a}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="ct-mensagem" className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f] block mb-2.5">
                                    {t.contacto.mensagem}
                                </label>
                                <textarea
                                    id="ct-mensagem"
                                    required
                                    minLength={5}
                                    rows={5}
                                    value={form.mensagem}
                                    onChange={set("mensagem")}
                                    data-testid="contacto-mensagem"
                                    className="w-full bg-[#070503] border border-[#c9a24b]/30 px-4 py-3 font-light text-[#efe6d6] placeholder:text-[#efe6d6]/30 focus:border-[#c9a24b] focus:outline-none transition-colors duration-300 resize-y"
                                    placeholder={t.contacto.mensagemPh}
                                />
                            </div>

                            {erro && (
                                <p className="flex items-center gap-2 text-sm font-light text-[#e0a0a0]" data-testid="contacto-erro">
                                    <TriangleAlert size={16} /> {erro}
                                </p>
                            )}

                            <button
                                type="submit"
                                disabled={estado === "enviando"}
                                data-testid="contacto-submit"
                                className="w-full inline-flex items-center justify-center gap-3 font-caps text-sm tracking-[0.25em] uppercase bg-[#5e1224] text-[#efe6d6] border border-[#c9a24b]/50 px-10 py-4 hover:bg-[#7a1a30] hover:border-[#c9a24b] transition-colors duration-400 disabled:opacity-40"
                            >
                                <Send size={15} strokeWidth={1.5} />
                                {estado === "enviando" ? t.contacto.aEnviar : t.contacto.enviar}
                            </button>
                        </form>
                    </Reveal>
                )}

                <Reveal delay={0.25} className="mt-12 text-center">
                    <p className="font-display italic font-light text-lg text-[#efe6d6]/60">{t.contacto.fecho}</p>
                </Reveal>
            </motion.main>
        </div>
    );
}
